import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";
import type { BookedFlightItineraryWithDepartureTime } from "@b2bportal/air-booking-api";
import { ChatPropertiesType } from "@b2bportal/chat-api";
import {
  DisruptionExerciseProgress,
  getDisruptionExerciseProgress,
  DisruptionUniversalExercise,
  setDisruptionExerciseProgress,
} from "@b2bportal/core-disruption";
import { fetchItinerary, fetchTripDetails } from "@b2bportal/core-flights";
import type { AppDispatch } from "@b2bportal/core-types";
import { trackEvent } from "@hopper-b2b/api";
import { PATH_TRIPS } from "@hopper-b2b/common-utils";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  CallState,
  CfarExerciseProgress,
  ChfarExerciseProgress,
  ClientName,
  FlightItineraryState,
  HotelItineraryState,
  type IApiConfig,
  MyTripsFilter,
  VIEWED_MY_TRIPS,
} from "@hopper-b2b/types";
import {
  B2BSpinner,
  IconComponent,
  IconName,
  LoadingPopup,
  Slot,
} from "@hopper-b2b/ui";
import {
  getEnvVariables,
  getHopperLocatorFromBookedFlightItinerary,
  updateApiConfigAction,
  useEnableLodging,
  useEnablePriceFreeze,
  useEnablePriceWatch,
} from "@hopper-b2b/utilities";
import { Box } from "@material-ui/core";
import clsx from "clsx";
import dayjs from "dayjs";
import { ClientContext } from "../../App";
import { useFlightTripDetailsInterception } from "../../hooks/useFlightTripDetailsInterception";
import { CfarExercise } from "../../modules/ancillaries/cfar/CfarExercise";
import { tripsApiConfigStoreKey } from "../../reducers";
import {
  setCfarExerciseProgress,
  setChfarExerciseProgress,
  setSelectedFlight,
  setTripsFilter,
} from "../TripsList/actions/actions";
import { ItinerariesModal } from "../TripsList/components/ItineraryList/components/ItinerariesModal";
import {
  getAirportMap,
  getCancellationCompleted,
  getCfarExerciseProgress,
  getChfarExerciseProgress,
} from "../TripsList/reducer/selectors";
import { parseQueryStringFromString } from "../TripsList/sagas/populateTripQueryParamsSaga";
import { CfarExercise as UberCfarExercise } from "./components/CfarExercise";
import { ChfarExercise } from "./components/ChfarExercise";
import { ItineraryList } from "./components/ItineraryList";
import { MobileFlightItineraryDetails } from "./components/ItineraryList/components/MobileFlightItineraryDetails";
import { MobileHotelItineraryDetails } from "./components/ItineraryList/components/MobileHotelItineraryDetails";
import { MobileFilterMenu } from "./components/MobileFilterMenu";
import { NoTripResults } from "./components/NoResults";
import { PriceFreezeList } from "./components/PriceFreezeList";
import { WatchDetailsModalContent } from "./components/WatchDetailsModalContent";
import type { TripsListConnectorProps } from "./container";
import { readInitialTripsFilter, resolveInitialTripsFilter } from "./helpers";
import "./styles.scss";

export interface ITripsListProps extends TripsListConnectorProps {
  isMobile?: boolean;
  apiConfig: IApiConfig;
  onSupportClick: (
    productId?: string,
    productType?: ChatPropertiesType,
    requestType?: string
  ) => void;
  goBack?: () => void;
  onGoToMyTrips?: () => void;
}

export const TripsList = ({
  fetchFlights,
  fetchHotels,
  // fetchCars,
  isMobile,
  selectedFlight,
  selectedHotel,
  selectedCar,
  selectedWatch,
  fetchFlightsCallState,
  fetchHotelsCallState,
  tripsFilter,
  tripsLoading,
  hasError,
  hasTripsToDisplay,
  openModal,
  listPriceFreeze,
  apiConfig,
  listWatches,
  setSelectedWatch,
  setOpenModal,
  onSupportClick,
  goBack,
  onGoToMyTrips,
}: ITripsListProps) => {
  const history = useHistory();
  const { search } = useLocation();

  const { t, brand } = useI18nContext();
  const dispatch = useDispatch<AppDispatch>();
  const clientName = getEnvVariables("clientName");
  const isUber = clientName === ClientName.UBER;
  const isNubank = clientName === ClientName.NUBANK;
  const clientContext = useContext(ClientContext);
  const airports = useSelector(getAirportMap);
  const cfarExerciseProgress = useSelector(getCfarExerciseProgress);
  const chfarExerciseProgress = useSelector(getChfarExerciseProgress);
  const disruptionExerciseProgress = useSelector(getDisruptionExerciseProgress);
  const cancellationCompleted = useSelector(getCancellationCompleted);
  const queryString = useMemo(
    () => parseQueryStringFromString(search),
    [search]
  );

  // Host interception of flight trip details (see
  // TripsClientAssetProps.onFlightTripDetailsOpen), e.g. Nubank redirecting
  // to the commerce portal [NU-403]. Suspended while an in-list exercise
  // flow owns the screen so the host cannot hijack an ongoing exercise.
  const exerciseFlowActive =
    cfarExerciseProgress !== CfarExerciseProgress.NotStarted ||
    chfarExerciseProgress !== ChfarExerciseProgress.NotStarted ||
    (disruptionExerciseProgress !== undefined &&
      disruptionExerciseProgress !== DisruptionExerciseProgress.NotStarted);

  const { showLoader: showFlightDetailsInterceptLoader } =
    useFlightTripDetailsInterception({
      itineraryId: selectedFlight?.bookedItinerary?.id,
      suspended: exerciseFlowActive,
    });
  const showFlightPriceFreeze = useEnablePriceFreeze();
  const showFlightPriceWatch =
    useEnablePriceWatch() && !brand.hideWatchesInTrips;
  const showHotels = useEnableLodging();
  const flightFetchSettled =
    fetchFlightsCallState === CallState.Success ||
    fetchFlightsCallState === CallState.Failed;
  const hotelFetchSettled =
    !showHotels ||
    fetchHotelsCallState === CallState.Success ||
    fetchHotelsCallState === CallState.Failed;
  const itineraryFetchesSettled = flightFetchSettled && hotelFetchSettled;
  // A deeplink can finish loading before its itinerary is selected. Keep the
  // host redirect surface covered during that gap, then release it if all
  // applicable fetches settle without finding a matching flight or hotel.
  // This also covers the equivalent pre-selection gap for hotel deeplinks.
  const resolvingInterceptedTripDeeplink =
    !exerciseFlowActive &&
    !hasError &&
    !itineraryFetchesSettled &&
    Boolean(clientContext?.onFlightTripDetailsOpen) &&
    Boolean(queryString.tripId) &&
    !selectedFlight &&
    !selectedHotel;
  const showFlightDetailsRedirectLoader =
    resolvingInterceptedTripDeeplink || showFlightDetailsInterceptLoader;
  const handleTripsRouting = useCallback(
    (selectedTrip: BookedFlightItineraryWithDepartureTime = null) => {
      dispatch(setSelectedFlight(selectedTrip));
      dispatch(
        setDisruptionExerciseProgress(DisruptionExerciseProgress.NotStarted)
      );
      dispatch(setCfarExerciseProgress(CfarExerciseProgress.NotStarted));
      if (selectedWatch) {
        setSelectedWatch(null);
        setOpenModal({ type: null, selectedItinerary: null });
        listWatches(history);
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      }
    },
    [
      dispatch,
      history,
      listWatches,
      selectedWatch,
      setOpenModal,
      setSelectedWatch,
    ]
  );

  // Consume the post-booking hand-off filter once on mount. Used only when the
  // URL has no explicit `tripsFilter` (in-app checkout -> /trips navigation can
  // drop the query param); external deep-links still resolve via `queryString`.
  const [storedInitialFilter] = useState<MyTripsFilter | undefined>(() =>
    readInitialTripsFilter()
  );

  const handleCfarProgress = useCallback(() => {
    if (cfarExerciseProgress === CfarExerciseProgress.Completed) {
      // Fire a message to redirect to the Uber flight search page when landing here with back button after completing a CFAR exercise
      const agentLocator =
        getHopperLocatorFromBookedFlightItinerary(selectedFlight);
      window.parent.postMessage(
        {
          type: "BOOKING_CANCELED",
          payload: { agentLocator },
        },
        import.meta.env["VITE_UBER_URL"]
      );
      dispatch(setCfarExerciseProgress(CfarExerciseProgress.NotStarted));
      dispatch(setSelectedFlight(null));
    } else {
      dispatch(setCfarExerciseProgress(CfarExerciseProgress.NotStarted));
    }
  }, [dispatch, cfarExerciseProgress, selectedFlight]);

  const handleChfarProgress = useCallback(
    (progress: ChfarExerciseProgress) => {
      dispatch(setChfarExerciseProgress(progress));
    },
    [dispatch]
  );

  const handleDisruptionProgress = useCallback(
    (progress: DisruptionExerciseProgress) => {
      dispatch(setDisruptionExerciseProgress(progress));
    },
    [dispatch]
  );

  // Fire a message to redirect to the Uber flight search page when landing here with back button after completing a self-serve cancellation
  useEffect(() => {
    if (cancellationCompleted && selectedFlight === null) {
      window.parent.postMessage(
        {
          type: "FINISH_SESSION",
          payload: null,
        },
        import.meta.env["VITE_UBER_URL"]
      );
    }
  }, [dispatch, cancellationCompleted, selectedFlight]);

  useEffect(() => {
    if (selectedFlight) {
      dispatch(
        fetchItinerary({
          itineraryId: selectedFlight.bookedItinerary.id,
          referenceDateTime: dayjs().toISOString(),
        })
      );
    }
  }, [selectedFlight, dispatch]);

  useEffect(() => {
    dispatch(updateApiConfigAction(tripsApiConfigStoreKey, apiConfig));
  }, [apiConfig, dispatch]);

  useEffect(() => {
    window.scrollTo(0, 0);
    trackEvent({
      eventName: VIEWED_MY_TRIPS,
      properties: {},
    });
  }, []);

  useEffect(() => {
    if (queryString.tripsFilter !== tripsFilter) {
      dispatch(
        setTripsFilter(
          resolveInitialTripsFilter(
            queryString.tripsFilter as MyTripsFilter,
            isNubank,
            storedInitialFilter
          )
        )
      );
    }
  }, [dispatch, queryString, tripsFilter, isNubank, storedInitialFilter]);

  useEffect(() => {
    if (!queryString.tripId || queryString.tripId === "") {
      handleTripsRouting();
    } else {
      if (
        !queryString.cfarExerciseProgress ||
        queryString.cfarExerciseProgress === "0"
      ) {
        handleCfarProgress();
      }
      if (
        !queryString.chfarExerciseProgress ||
        queryString.chfarExerciseProgress === "0"
      ) {
        handleChfarProgress(ChfarExerciseProgress.NotStarted);
      }
      if (
        !queryString.disruptionExerciseProgress ||
        queryString.disruptionExerciseProgress === "0"
      ) {
        handleDisruptionProgress(DisruptionExerciseProgress.NotStarted);
      }
    }
  }, [
    handleCfarProgress,
    handleChfarProgress,
    handleDisruptionProgress,
    handleTripsRouting,
    queryString,
    selectedWatch?.id,
  ]);

  const fetchAllItineraries = useCallback(() => {
    fetchFlights(
      {
        states: [
          FlightItineraryState.Canceled,
          FlightItineraryState.Future,
          FlightItineraryState.Present,
          FlightItineraryState.Past,
        ],
        referenceDateTime: dayjs().format(),
      },
      history
    );
    // TODO: Check why this throws off the formatting
    showHotels &&
      fetchHotels(
        {
          states: [
            HotelItineraryState.Canceled,
            HotelItineraryState.Future,
            HotelItineraryState.Present,
            HotelItineraryState.Past,
          ],
          referenceDateTime: dayjs().format(),
        },
        history
      );
  }, [fetchFlights, fetchHotels, history, showHotels]);

  useEffect(() => {
    fetchAllItineraries();
    if (showFlightPriceFreeze) listPriceFreeze();
    if (showFlightPriceWatch) listWatches(history);
  }, [
    history,
    fetchAllItineraries,
    listPriceFreeze,
    listWatches,
    showFlightPriceFreeze,
    showFlightPriceWatch,
  ]);

  const filteredListComponent = useMemo(() => {
    switch (tripsFilter) {
      case MyTripsFilter.UPCOMING_TRIPS:
      case MyTripsFilter.FLIGHTS:
      case MyTripsFilter.HOTELS:
      case MyTripsFilter.PAST_TRIPS:
        return (
          <>
            <PriceFreezeList />
            <ItineraryList isMobile={isMobile} />
          </>
        );

      case MyTripsFilter.PRIZE_FREEZES:
        return <PriceFreezeList />;
      case MyTripsFilter.WATCHED_TRIPS:
        return <ItineraryList isMobile={isMobile} />;
      default:
        return null;
    }
  }, [isMobile, tripsFilter]);

  const selectedItinerary = useMemo(() => {
    const renderCfarExercise = !(
      cfarExerciseProgress === CfarExerciseProgress.NotStarted
    );

    const renderChfarExercise = !(
      chfarExerciseProgress === ChfarExerciseProgress.NotStarted
    );

    const renderDisruptionExercise =
      !(disruptionExerciseProgress === DisruptionExerciseProgress.NotStarted) &&
      !(disruptionExerciseProgress === undefined);

    return (
      <>
        {selectedFlight ? (
          renderCfarExercise ? (
            isUber ? (
              <UberCfarExercise flight={selectedFlight} />
            ) : (
              <CfarExercise
                flight={selectedFlight}
                setOpenModal={setOpenModal}
              />
            )
          ) : renderChfarExercise ? (
            <ChfarExercise flight={selectedFlight} />
          ) : renderDisruptionExercise ? (
            <DisruptionUniversalExercise
              disruptedFlight={selectedFlight}
              onContactSupport={() => {
                onSupportClick(
                  selectedFlight.bookedItinerary.id,
                  ChatPropertiesType.Air,
                  "General"
                );
              }}
              handleExerciseCompleted={() => {
                setOpenModal({ type: null, selectedItinerary: null });
                // reload the trips and navigate to selected itinerray
                window.location.href = `/${PATH_TRIPS}/?tripId=${selectedFlight.bookedItinerary.id}`;
              }}
              displayExternalLinkAsModal={true}
              airports={airports}
              fetchTripDetails={fetchTripDetails}
            />
          ) : showFlightDetailsRedirectLoader ? (
            <LoadingPopup
              open
              indicator={B2BSpinner}
              message={t("fetchingTrips")}
              classes={["trips-loading-modal", ...(isMobile ? ["mobile"] : [])]}
            />
          ) : (
            <MobileFlightItineraryDetails
              onSupportClick={onSupportClick}
              onGoToMyTrips={onGoToMyTrips}
            />
          )
        ) : null}
        {selectedWatch ? <WatchDetailsModalContent /> : null}
        {selectedHotel ? (
          <MobileHotelItineraryDetails
            onSupportClick={onSupportClick}
            onGoToMyTrips={onGoToMyTrips}
          />
        ) : null}
        <ItinerariesModal isMobile={isMobile} onSupportClick={onSupportClick} />
      </>
    );
  }, [
    cfarExerciseProgress,
    chfarExerciseProgress,
    disruptionExerciseProgress,
    isMobile,
    onGoToMyTrips,
    onSupportClick,
    selectedFlight,
    selectedHotel,
    selectedWatch,
    isUber,
    setOpenModal,
    airports,
    showFlightDetailsRedirectLoader,
    t,
  ]);

  const hasSelectedItinerary = Boolean(
    selectedFlight || selectedHotel || selectedCar || selectedWatch
  );
  const showSelectedItinerary =
    Boolean(isMobile && hasSelectedItinerary) ||
    Boolean(selectedFlight && showFlightDetailsRedirectLoader);

  return (
    <>
      {showSelectedItinerary ? (
        selectedItinerary
      ) : (
        <Box
          className={clsx(
            { mobile: isMobile },
            "trips-list",
            "new",
            getEnvVariables("clientName")
          )}
        >
          {tripsLoading ||
          tripsLoading === null ||
          resolvingInterceptedTripDeeplink ? (
            <Slot
              id="trips-list-loading-screen"
              open={true}
              message={t("fetchingTrips")}
              className={[
                "trips-loading-modal",
                ...(isMobile ? ["mobile"] : []),
              ]}
              component={
                <LoadingPopup
                  open={true}
                  indicator={B2BSpinner}
                  message={t("fetchingTrips")}
                  classes={[
                    "trips-loading-modal",
                    ...(isMobile ? ["mobile"] : []),
                  ]}
                />
              }
            />
          ) : (
            <>
              {isNubank ? (
                <div className="trips-list-header">
                  <div
                    className="trips-list-header-button"
                    role="button"
                    tabIndex={0}
                    onClick={goBack}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        goBack();
                      }
                    }}
                  >
                    {clientContext?.assets?.back ? (
                      // eslint-disable-next-line jsx-a11y/alt-text
                      <img
                        src={clientContext.assets.back}
                        className="back-arrow"
                      />
                    ) : (
                      <IconComponent
                        ariaLabel="back icon"
                        className="back-arrow"
                        name={isNubank ? IconName.Chevron : IconName.ArrowRight}
                      />
                    )}
                  </div>
                  <div className="trips-list-header-title">{t("myTrips")}</div>
                </div>
              ) : null}
              <MobileFilterMenu />
              <div
                id={`${tripsFilter}-tab`}
                role="tabpanel"
                aria-labelledby={tripsFilter}
              >
                <NoTripResults isMobile={isMobile} />
                {!hasError &&
                  (hasTripsToDisplay || openModal) &&
                  filteredListComponent}
              </div>
            </>
          )}
        </Box>
      )}
    </>
  );
};
